import { CreatedIcon, TagsIcon } from '@blocksuite/icons/rc';
import { Menu, MenuItem, PiqueLogo, Scrollable } from '@pique/component';
import { useBlockSuiteDocMeta } from '@pique/core/hooks/use-block-suite-page-meta';
import { AttendeesService } from '@pique/core/modules/properties';
import type { SerializedAttendee } from '@pique/core/modules/properties/services/schema';
import { deduplicateAttendees } from '@pique/core/modules/properties/services/utils';
import { ShareDocsListService } from '@pique/core/modules/share-doc';
import type { Filter, Tag } from '@pique/env/filter';
import { PublicPageMode } from '@pique/graphql';
import { Trans, useI18n } from '@pique/i18n';
import {
  useLiveData,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import { Users, Video } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  adaptSerializedAttendees,
  AttendeeAvatars,
} from '../../../pique/page-properties/attendee-avatars';
import { FilterList } from '../../filter';
import { createDefaultFilter, vars } from '../../filter/vars';
import { usePageHeaderColsDef } from '../../header-col-def';
import { filterPageByRules } from '../../use-collection-manager';
import { PageListItemRenderer } from '../../page-group';
import { ListTableHeader } from '../../page-header';
import type { BaseSelectorDialogProps } from '../../selector';
import { SelectorLayout } from '../../selector/selector-layout';
import type { ListItem } from '../../types';
import { VirtualizedList } from '../../virtualized-list';
import * as styles from './edit-collection.css';
import { useFilter } from './use-filter';
import { useSearch } from './use-search';

const filterIcons: Record<string, React.ReactNode> = {
  Created: <CreatedIcon />,
  Tags: <TagsIcon />,
  Attendees: <Users size={16} />,
  Title: <Video size={16} />,
};

export const SelectPage = ({
  init = [],
  initFilters = [],
  onConfirm,
  onCancel,
  onChange: propsOnChange,
  onFiltersChange,
  buttons,
}: {
  onChange?: (values: string[]) => void;
  initFilters?: Filter[];
  onFiltersChange?: (filters: Filter[]) => void;
  confirmText?: ReactNode;
  header?: ReactNode;
  buttons?: ReactNode;
} & BaseSelectorDialogProps<string[]>) => {
  const t = useI18n();
  const [value, setValue] = useState(init);
  const onChange = useCallback(
    (value: string[]) => {
      propsOnChange?.(value);
      setValue(value);
    },
    [propsOnChange]
  );
  const confirm = useCallback(() => {
    onConfirm?.(value);
  }, [value, onConfirm]);
  const clearSelected = useCallback(() => {
    onChange([]);
  }, [onChange]);
  const { workspaceService, shareDocsListService, attendeesService } =
    useServices({
      ShareDocsListService,
      WorkspaceService,
      AttendeesService,
    });
  const shareDocs = useLiveData(shareDocsListService.shareDocs?.list$);
  const workspace = workspaceService.workspace;
  const docCollection = workspace.docCollection;
  const pageMetas = useBlockSuiteDocMeta(docCollection);
  const allAttendeesMap = useLiveData(attendeesService.attendees$);

  useEffect(() => {
    shareDocsListService.shareDocs?.revalidate();
  }, [shareDocsListService.shareDocs]);

  const getPublicMode = useCallback(
    (id: string): 'edgeless' | 'page' | undefined => {
      const mode = shareDocs?.find(
        (shareDoc: { id: string; mode: PublicPageMode }) => shareDoc.id === id
      )?.mode;
      if (mode === PublicPageMode.Edgeless) {
        return 'edgeless';
      } else if (mode === PublicPageMode.Page) {
        return 'page';
      } else {
        return undefined;
      }
    },
    [shareDocs]
  );

  const pageHeaderColsDef = usePageHeaderColsDef();

  const pageDataList = useMemo(
    () =>
      pageMetas.map(meta => {
        const pageAttendees: SerializedAttendee[] =
          allAttendeesMap[meta.id] ?? [];
        return {
          meta,
          publicMode: getPublicMode(meta.id),
          favorite: false,
          attendees: pageAttendees.map(a => a.id),
          pageTitle: meta.title || 'Untitled',
        };
      }),
    [pageMetas, allAttendeesMap, getPublicMode]
  );

  const {
    createFilter,
    filters,
    updateFilters: setFilters,
    filteredList,
  } = useFilter(pageDataList, initFilters);

  // Track previous filter count to detect when filters are added
  const prevFilterCountRef = useRef(initFilters.length);

  const updateFilters = useCallback(
    (newFilters: Filter[]) => {
      const prevCount = prevFilterCountRef.current;
      const newCount = newFilters.length;
      setFilters(newFilters);
      onFiltersChange?.(newFilters);

      // Auto-select all filtered docs when a filter is added (not removed)
      if (newCount > prevCount) {
        // Compute filtered list using NEW filters directly (not stale closure)
        const newFilteredIds = pageDataList
          .filter(pageData => {
            if (pageData.meta.trash) return false;
            return filterPageByRules(newFilters, [], pageData);
          })
          .map(pageData => pageData.meta.id);

        if (newFilteredIds.length > 0) {
          const newSelection = [...new Set([...value, ...newFilteredIds])];
          onChange(newSelection);
        }
      }
      prevFilterCountRef.current = newCount;
    },
    [setFilters, onFiltersChange, pageDataList, value, onChange]
  );
  const { searchText, updateSearchText, searchedList } =
    useSearch(filteredList);

  const activeFilterNames = useMemo(
    () => new Set(filters.map(f => f.left.name as string)),
    [filters]
  );

  const tags: Tag[] = docCollection.meta.properties.tags?.options ?? [];
  const allAttendees = useMemo(() => {
    const all: SerializedAttendee[] = [];
    for (const pageAttendees of Object.values(
      allAttendeesMap
    ) as SerializedAttendee[][]) {
      all.push(...pageAttendees);
    }
    return deduplicateAttendees(all).sort((a, b) =>
      (a.name || a.email).localeCompare(b.name || b.email)
    );
  }, [allAttendeesMap]);

  const getFilterValue = useCallback(
    (filterName: string): string[] => {
      const filter = filters.find(f => f.left.name === filterName);
      if (!filter || !filter.args[0]) return [];
      return (filter.args[0].value as string[]) ?? [];
    },
    [filters]
  );

  const updateFilterValue = useCallback(
    (filterName: string, newValue: string[]) => {
      const existingFilter = filters.find(f => f.left.name === filterName);
      if (existingFilter) {
        if (newValue.length === 0) {
          updateFilters(filters.filter(f => f.left.name !== filterName));
        } else {
          updateFilters(
            filters.map(f =>
              f.left.name === filterName
                ? {
                    ...f,
                    args: [{ type: 'literal' as const, value: newValue }],
                  }
                : f
            )
          );
        }
      } else if (newValue.length > 0) {
        const variable = vars.find(v => v.name === filterName);
        if (variable) {
          const newFilter = createDefaultFilter(
            variable,
            docCollection.meta.properties
          );
          newFilter.args = [{ type: 'literal', value: newValue }];
          createFilter(newFilter);
        }
      }
    },
    [filters, updateFilters, createFilter, docCollection.meta.properties]
  );

  const handleFilterChipClick = useCallback(
    (varName: string) => {
      if (activeFilterNames.has(varName)) {
        updateFilters(filters.filter(f => f.left.name !== varName));
      } else {
        const variable = vars.find(v => v.name === varName);
        if (variable) {
          const newFilter = createDefaultFilter(
            variable,
            docCollection.meta.properties
          );
          createFilter(newFilter);
        }
      }
    },
    [
      activeFilterNames,
      filters,
      updateFilters,
      createFilter,
      docCollection.meta.properties,
    ]
  );

  const pageHeaderRenderer = useCallback(() => {
    return <ListTableHeader headerCols={pageHeaderColsDef} />;
  }, [pageHeaderColsDef]);

  const pageItemRenderer = useCallback((item: ListItem) => {
    return <PageListItemRenderer {...item} />;
  }, []);

  const selectedTagIds = getFilterValue('Tags');
  const selectedAttendeeIds = getFilterValue('Attendees');
  const selectedMeetingIds = getFilterValue('Title');

  const tagsMenuItems = (
    <Scrollable.Root>
      <Scrollable.Viewport style={{ maxHeight: 300, minWidth: 200 }}>
        {tags.length === 0 ? (
          <MenuItem disabled>No tags</MenuItem>
        ) : (
          tags.map(tag => (
            <MenuItem
              key={tag.id}
              checked={selectedTagIds.includes(tag.id)}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                const isSelected = selectedTagIds.includes(tag.id);
                const newValue = isSelected
                  ? selectedTagIds.filter(id => id !== tag.id)
                  : [...selectedTagIds, tag.id];
                updateFilterValue('Tags', newValue);
              }}
              prefixIcon={
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: tag.color,
                    flexShrink: 0,
                  }}
                />
              }
            >
              {tag.value}
            </MenuItem>
          ))
        )}
      </Scrollable.Viewport>
    </Scrollable.Root>
  );

  const attendeesMenuItems = (
    <Scrollable.Root>
      <Scrollable.Viewport style={{ maxHeight: 300, minWidth: 240 }}>
        {allAttendees.length === 0 ? (
          <MenuItem disabled>No attendees</MenuItem>
        ) : (
          allAttendees.map(attendee => (
            <MenuItem
              key={attendee.id}
              checked={selectedAttendeeIds.includes(attendee.id)}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                const isSelected = selectedAttendeeIds.includes(attendee.id);
                const newValue = isSelected
                  ? selectedAttendeeIds.filter(id => id !== attendee.id)
                  : [...selectedAttendeeIds, attendee.id];
                updateFilterValue('Attendees', newValue);
              }}
              prefixIcon={
                <AttendeeAvatars
                  attendees={adaptSerializedAttendees([attendee])}
                  maxDisplay={1}
                  size="small"
                />
              }
            >
              {attendee.name || attendee.email.split('@')[0]}
            </MenuItem>
          ))
        )}
      </Scrollable.Viewport>
    </Scrollable.Root>
  );

  const allMeetings = useMemo(() => {
    const uniqueTitles = new Set<string>();
    pageMetas
      .filter(meta => !meta.trash)
      .forEach(meta => {
        const title = meta.title || 'Untitled';
        uniqueTitles.add(title);
      });
    return Array.from(uniqueTitles)
      .sort((a, b) => a.localeCompare(b))
      .map(title => ({ title }));
  }, [pageMetas]);

  const meetingsMenuItems = (
    <Scrollable.Root>
      <Scrollable.Viewport style={{ maxHeight: 300, minWidth: 240 }}>
        {allMeetings.length === 0 ? (
          <MenuItem disabled>No meetings</MenuItem>
        ) : (
          allMeetings.map(meeting => (
            <MenuItem
              key={meeting.title}
              checked={selectedMeetingIds.includes(meeting.title)}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                const isSelected = selectedMeetingIds.includes(meeting.title);
                const newValue = isSelected
                  ? selectedMeetingIds.filter(t => t !== meeting.title)
                  : [...selectedMeetingIds, meeting.title];
                updateFilterValue('Title', newValue);
              }}
            >
              {meeting.title}
            </MenuItem>
          ))
        )}
      </Scrollable.Viewport>
    </Scrollable.Root>
  );

  return (
    <SelectorLayout
      searchPlaceholder={t['com.pique.editCollection.search.placeholder']()}
      selectedCount={value.length}
      onSearch={updateSearchText}
      onClear={clearSelected}
      onCancel={onCancel}
      onConfirm={confirm}
      actions={buttons}
    >
      <div className={styles.pagesTab}>
        <div className={styles.filterRow}>
          <span className={styles.filterRowLabel}>
            {t['com.pique.filter']()}
          </span>
          <div
            className={styles.filterChip}
            data-active={activeFilterNames.has('Created')}
            onClick={() => handleFilterChipClick('Created')}
          >
            <span className={styles.filterChipIcon}>
              {filterIcons['Created']}
            </span>
            <span>Created</span>
          </div>
          <Menu items={tagsMenuItems}>
            <div
              className={styles.filterChip}
              data-active={selectedTagIds.length > 0}
            >
              <span className={styles.filterChipIcon}>
                {filterIcons['Tags']}
              </span>
              <span>
                Tags
                {selectedTagIds.length > 0 ? ` (${selectedTagIds.length})` : ''}
              </span>
            </div>
          </Menu>
          <Menu items={attendeesMenuItems}>
            <div
              className={styles.filterChip}
              data-active={selectedAttendeeIds.length > 0}
            >
              <span className={styles.filterChipIcon}>
                {filterIcons['Attendees']}
              </span>
              <span>
                Attendees
                {selectedAttendeeIds.length > 0
                  ? ` (${selectedAttendeeIds.length})`
                  : ''}
              </span>
            </div>
          </Menu>
          <Menu items={meetingsMenuItems}>
            <div
              className={styles.filterChip}
              data-active={selectedMeetingIds.length > 0}
            >
              <span className={styles.filterChipIcon}>
                {filterIcons['Title']}
              </span>
              <span>
                Meeting
                {selectedMeetingIds.length > 0
                  ? ` (${selectedMeetingIds.length})`
                  : ''}
              </span>
            </div>
          </Menu>
        </div>
        {filters.length > 0 && (
          <div className={styles.activeFiltersRow}>
            <FilterList
              propertiesMeta={docCollection.meta.properties}
              value={filters}
              onChange={updateFilters}
            />
          </div>
        )}
        {searchedList.length ? (
          <VirtualizedList
            className={styles.pageList}
            items={searchedList}
            docCollection={docCollection}
            selectable
            onSelectedIdsChange={onChange}
            selectedIds={value}
            itemRenderer={pageItemRenderer}
            headerRenderer={pageHeaderRenderer}
          />
        ) : (
          <EmptyList search={searchText} />
        )}
      </div>
    </SelectorLayout>
  );
};
export const EmptyList = ({ search }: { search?: string }) => {
  const t = useI18n();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <PiqueLogo />
      <div
        style={{
          margin: '18px 0',
          fontSize: 20,
          lineHeight: '28px',
          fontWeight: 600,
        }}
      >
        {t['com.pique.selectPage.empty']()}
      </div>
      {search ? (
        <div
          className={styles.ellipsis}
          style={{ maxWidth: 300, fontSize: 15, lineHeight: '24px' }}
        >
          <Trans i18nKey="com.pique.selectPage.empty.tips" values={{ search }}>
            No page titles contain
            <span
              style={{ fontWeight: 600, color: 'var(--affine-primary-color)' }}
            >
              search
            </span>
          </Trans>
        </div>
      ) : null}
    </div>
  );
};
